import React, { useState, useEffect } from 'react';
import { 
  Search, Smartphone, MapPin, User, ShieldAlert, 
  ChevronRight, FileText, Calendar, Mail, Fingerprint, 
  Globe, ShieldCheck, Lock, Activity, RefreshCw
} from 'lucide-react';

/**
 * 🛰️ SAHELGUARD EMPIRE CRM - V7.0.2 (GLOBAL & FINAL)
 * Traçabilité IMEI, Gestion KYC & Désinstallation Distante DO
 * Connecté à D1 Bank via sg-core-api
 * URL Forcée : https://sahelguard-api.sahelguard.workers.dev
 */

const EmpireCRM = ({ apiUrl }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isUninstalling, setIsUninstalling] = useState(false);

  // 📡 SYNCHRO D1 : RÉCUPÉRATION DES DOSSIERS CLIENTS
  const fetchClients = async () => {
    setLoading(true);
    // On utilise l'URL du worker déployé par défaut
    const baseUri = "https://sahelguard-api.sahelguard.workers.dev";
    
    try {
      console.log("Connexion à l'Empire D1...");
      const response = await fetch(`${baseUri}/api/clients`);
      const data = await response.json();
      
      if (data.clients) {
        setClients(data.clients);
        console.log(`${data.clients.length} terminaux synchronisés.`);
      }
    } catch (e) {
      console.error("Erreur de liaison CRM-D1:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  // 🛡️ PROTOCOLE SOUVERAIN : DÉSINSTALLATION DO (DEVICE OWNER)
  // Instruction 04/02/2026 : Seuls les Super Admins (+22675255416, +22662993339)
  const handleRemoteUninstall = async (client) => {
    const confirmation = window.confirm(
      `ALERTE SÉCURITÉ SAHELGUARD\n\nConfirmez-vous la désinstallation forcée du Device Owner sur le terminal :\nPropriétaire : ${client.name}\nIMEI : ${client.imei}\n\nCette action est irréversible.`
    );

    if (!confirmation) return;

    setIsUninstalling(true);
    const baseUri = "https://sahelguard-api.sahelguard.workers.dev";
    
    try {
      const response = await fetch(`${baseUri}/api/commands/uninstall`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          adminNumbers: ["+22675255416", "+22662993339"],
          targetImei: client.imei,
          targetPhone: client.phone_number,
          pinType: "6-DIGIT-ADMIN",
          timestamp: new Date().toISOString()
        })
      });

      if (response.ok) {
        alert("🚀 Commande UNINSTALL_DO transmise au réseau. Le terminal sera libéré dès réception du signal.");
        fetchClients();
      }
    } catch (e) {
      alert("❌ Échec de la transmission : Vérifiez la connexion au Worker.");
    } finally {
      setIsUninstalling(false);
    }
  };

  // 🔍 MOTEUR DE RECHERCHE (NOM OU IMEI)
  const filteredClients = clients.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    c.imei?.includes(searchTerm) ||
    c.phone_number?.includes(searchTerm)
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white pt-32 pb-20 px-6 md:px-12 font-sans overflow-x-hidden">
      <div className="max-w-[1600px] mx-auto">
        
        {/* HEADER CRM */}
        <div className="flex flex-col md:flex-row justify-between items-center mb-16 gap-8">
          <div>
            <h1 className="text-5xl font-black italic uppercase tracking-tighter flex items-center gap-4">
              <ShieldCheck className="text-emerald-500" size={40} /> Empire CRM
            </h1>
            <p className="text-emerald-500/50 text-[10px] font-black uppercase tracking-[0.5em] mt-2">Traçabilité & Kyc Souverain</p>
          </div>
          
          <div className="relative w-full max-w-xl">
            <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-emerald-500/50" size={20} />
            <input 
              type="text"
              placeholder="Rechercher IMEI, Nom ou N° Téléphone..."
              className="w-full bg-white/5 border border-white/10 rounded-full py-6 pl-16 pr-8 text-sm font-black uppercase tracking-widest outline-none focus:border-emerald-500 focus:bg-white/10 transition-all placeholder:text-white/20"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="grid xl:grid-cols-3 gap-12">
          
          {/* 📋 LISTE DES DOSSIERS */}
          <div className="xl:col-span-1 space-y-4 max-h-[70vh] overflow-y-auto pr-4 custom-scrollbar">
            {loading ? (
              <div className="flex flex-col items-center py-20 opacity-30">
                <RefreshCw className="animate-spin mb-4" />
                <p className="text-[10px] font-black uppercase tracking-widest italic">Scan de la base D1...</p>
              </div>
            ) : filteredClients.length > 0 ? filteredClients.map(client => (
              <button 
                key={client.id}
                onClick={() => setSelectedClient(client)}
                className={`w-full text-left p-6 rounded-[2.5rem] border transition-all flex items-center justify-between group ${selectedClient?.id === client.id ? 'bg-emerald-500 border-emerald-500 text-black shadow-[0_0_30px_rgba(16,185,129,0.2)]' : 'bg-white/5 border-white/5 hover:border-white/20'}`}
              >
                <div className="flex items-center gap-4">
                  <div className={`p-4 rounded-2xl ${selectedClient?.id === client.id ? 'bg-black text-emerald-500' : 'bg-black text-white'}`}>
                    <User size={20} />
                  </div>
                  <div>
                    <h4 className="text-xs font-black uppercase tracking-tighter">{client.name}</h4>
                    <p className={`text-[9px] font-mono mt-1 ${selectedClient?.id === client.id ? 'text-black/60' : 'text-slate-500'}`}>IMEI: {client.imei}</p>
                  </div>
                </div>
                <ChevronRight size={18} className={selectedClient?.id === client.id ? 'text-black' : 'text-white/20'} />
              </button>
            )) : (
              <div className="py-20 text-center opacity-20">
                <p className="text-xs font-black uppercase italic tracking-widest">Aucun client trouvé</p>
              </div>
            )}
          </div>

          {/* 📑 FICHE CLIENT DÉTAILLÉE */}
          <div className="xl:col-span-2">
            {selectedClient ? (
              <div className="bg-white/5 border border-white/10 rounded-[4rem] p-10 md:p-14 animate-in fade-in slide-in-from-right-10 duration-500">
                
                <div className="flex flex-col md:flex-row justify-between items-start gap-8 mb-16 border-b border-white/5 pb-12">
                  <div>
                    <h2 className="text-6xl font-black italic uppercase tracking-tighter mb-4">{selectedClient.name}</h2>
                    <div className="flex gap-3">
                       <span className="bg-emerald-500/10 text-emerald-500 px-4 py-2 rounded-xl text-[9px] font-black tracking-[0.2em] uppercase border border-emerald-500/20">Protégé</span>
                       {selectedClient.security_status === 'SIM_CHANGED_ALERT' && (
                         <span className="bg-red-500/10 text-red-500 px-4 py-2 rounded-xl text-[9px] font-black tracking-[0.2em] uppercase border border-red-500/20 animate-pulse italic">SIM Change Détecté</span>
                       )}
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-4">
                    <button 
                      onClick={() => handleRemoteUninstall(selectedClient)}
                      disabled={isUninstalling}
                      className="px-8 py-5 bg-red-600 text-white rounded-3xl text-[10px] font-black uppercase tracking-widest hover:bg-red-700 transition-all flex items-center gap-3 shadow-xl shadow-red-900/20 disabled:opacity-50"
                    >
                      {isUninstalling ? <RefreshCw className="animate-spin" size={16}/> : <ShieldAlert size={16} />}
                      Force Uninstall DO
                    </button>
                    <button className="px-8 py-5 bg-white text-black rounded-3xl text-[10px] font-black uppercase tracking-widest hover:bg-emerald-500 transition-all shadow-xl">
                      Éditer Dossier
                    </button>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-10">
                  {/* Identité */}
                  <div className="bg-black/40 p-10 rounded-[3.5rem] border border-white/5 relative overflow-hidden group">
                    <Fingerprint size={100} className="absolute -right-8 -top-8 text-white/5 group-hover:text-emerald-500/10 transition-colors" />
                    <h3 className="text-[11px] font-black uppercase text-emerald-500 mb-8 flex items-center gap-3 tracking-widest">
                      <Smartphone size={16} /> Identité Appareil
                    </h3>
                    <div className="space-y-8 relative z-10">
                      <DataRow icon={<Fingerprint />} label="IMEI Souverain" value={selectedClient.imei} />
                      <DataRow icon={<FileText />} label="N° CNI / Passport" value={selectedClient.cni} />
                      <DataRow icon={<Calendar />} label="Date de Naissance" value={selectedClient.dob} />
                      <DataRow icon={<Lock />} label="N° Téléphone Actuel" value={selectedClient.phone_number} />
                    </div>
                  </div>

                  {/* Localisation */}
                  <div className="bg-black/40 p-10 rounded-[3.5rem] border border-white/5 relative overflow-hidden group">
                    <Globe size={100} className="absolute -right-8 -top-8 text-white/5 group-hover:text-blue-500/10 transition-colors" />
                    <h3 className="text-[11px] font-black uppercase text-blue-400 mb-8 flex items-center gap-3 tracking-widest">
                      <MapPin size={16} /> Localisation & Contact
                    </h3>
                    <div className="space-y-8 relative z-10">
                      <DataRow icon={<Globe />} label="Pays / Ville" value={`${selectedClient.country || "Burkina Faso"} / ${selectedClient.city}`} />
                      <DataRow icon={<MapPin />} label="Adresse Résidence" value={selectedClient.address} />
                      <DataRow icon={<Mail />} label="Email Dossier" value={selectedClient.email} />
                    </div>
                  </div>
                </div>

                {/* Bloc Secours */}
                <div className="mt-10 p-10 bg-emerald-500/5 border border-emerald-500/10 rounded-[3.5rem] flex flex-col md:flex-row items-center justify-between gap-8">
                  <div>
                    <h3 className="text-[11px] font-black uppercase text-emerald-500 mb-2 tracking-widest flex items-center gap-2">
                      <Activity size={16} /> Numéros de Secours (Art. 5)
                    </h3>
                    <p className="text-[9px] font-bold text-slate-500 uppercase italic text-center md:text-left">
                      Seuls ces 3 numéros peuvent exécuter des commandes via leur PIN 4-digit.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    {(selectedClient.emergency_numbers?.split(',') || []).map((num, i) => (
                      <div key={i} className="px-6 py-3 bg-black rounded-2xl text-[11px] font-black border border-white/10 hover:border-emerald-500/50 transition-colors text-emerald-500">
                        {num.trim()}
                      </div>
                    ))}
                    {!selectedClient.emergency_numbers && <p className="text-[10px] italic opacity-30 font-bold uppercase tracking-widest">Aucun numéro enregistré</p>}
                  </div>
                </div>
              </div>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-center opacity-20 p-24 border-4 border-dashed border-white/5 rounded-[5rem]">
                <Smartphone size={100} className="mb-8" />
                <h3 className="text-3xl font-black italic uppercase tracking-widest">Sélectionner un Terminal</h3>
                <p className="text-sm font-bold uppercase mt-4 tracking-widest">Accès aux données KYC & Commandes Souveraines</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const DataRow = ({ icon, label, value }) => (
  <div className="flex items-center gap-6 group/row">
    <div className="p-3 bg-white/5 rounded-xl text-slate-500 group-hover/row:bg-white/10 group-hover/row:text-white transition-all transform group-hover/row:scale-110">
      {React.cloneElement(icon, { size: 16 })}
    </div>
    <div>
      <p className="text-[8px] font-black text-slate-600 uppercase tracking-[0.2em] mb-1">{label}</p>
      <p className="text-sm font-black text-white/90 uppercase tracking-tighter">{value || "Non renseigné"}</p>
    </div>
  </div>
);

export default EmpireCRM;